// =============================================================================
// A despesa por categoria, todos os centros de custo somados
//
// Pedido de 27/10: a aba Despesa da Visão financeira com os blocos por
// categoria do Painel do Gestor. Decidido com o Enzo: montar pela coordenada
// (CC + categoria + competência, a `v_orcado_realizado_mes` da 044) e não pelas
// linhas orçamentárias, como o Painel faz — assim a Despesa soma o mesmo que a
// grade do Orçado vs Realizado, e um lançamento rateado entre áreas entra
// inteiro, cada parte no seu CC (o motor guarda o casamento numa linha só).
//
// Aqui só se somam subtotais que a view já produziu, nunca lançamentos.
// =============================================================================

import type { OrcadoRealizadoLinha } from "./api.ts";
import type { Tom } from "./formato.ts";

export type ValoresCategoria = {
  orcado: bigint;
  realizado: bigint;
  comprometido: bigint;
  /** Orçado − realizado − comprometido: o "Disponível" do AF-01, que pode ser negativo. */
  saldo: bigint;
};

export type CentroDaCategoria = ValoresCategoria & {
  centro_custo_id: string;
  codigo: string;
  nome: string;
};

export type CategoriaConsolidada = ValoresCategoria & {
  chave: string;
  /** Nulo no lançamento sem categoria (044). */
  categoria_id: string | null;
  codigo: string | null;
  nome: string;
  /** A categoria aberta por centro de custo, do maior realizado para o menor. */
  centros: CentroDaCategoria[];
};

const zero = (): ValoresCategoria => ({ orcado: 0n, realizado: 0n, comprometido: 0n, saldo: 0n });

function somar(t: ValoresCategoria, l: OrcadoRealizadoLinha) {
  const o = BigInt(l.orcado_centavos);
  const r = BigInt(l.realizado_centavos ?? "0");
  const c = BigInt(l.comprometido_centavos ?? "0");
  t.orcado += o;
  t.realizado += r;
  t.comprometido += c;
  t.saldo += o - r - c;
}

const porMaiorRealizado = (a: ValoresCategoria, b: ValoresCategoria) =>
  a.realizado === b.realizado ? 0 : a.realizado > b.realizado ? -1 : 1;

/**
 * As categorias nos meses do período, com cada centro de custo dentro. Fica de
 * fora a categoria sem orçado, realizado e comprometido no período. Ordem pelo
 * código do plano de contas, como a grade.
 */
export function consolidarPorCategoria(
  linhas: OrcadoRealizadoLinha[],
  competencias: string[],
): CategoriaConsolidada[] {
  const meses = new Set(competencias);
  const porCategoria = new Map<string, CategoriaConsolidada & { porCc: Map<string, CentroDaCategoria> }>();
  for (const l of linhas) {
    if (!meses.has(l.competencia) || l.natureza === "receita") continue;
    const chave = l.categoria_id ?? "sem-categoria";
    let cat = porCategoria.get(chave);
    if (!cat) {
      cat = {
        chave,
        categoria_id: l.categoria_id,
        codigo: l.categoria_codigo,
        nome: l.categoria_nome,
        ...zero(),
        centros: [],
        porCc: new Map(),
      };
      porCategoria.set(chave, cat);
    }
    somar(cat, l);
    let cc = cat.porCc.get(l.centro_custo_id);
    if (!cc) {
      cc = { centro_custo_id: l.centro_custo_id, codigo: l.centro_custo_codigo, nome: l.centro_custo_nome, ...zero() };
      cat.porCc.set(l.centro_custo_id, cc);
    }
    somar(cc, l);
  }
  const vazio = (v: ValoresCategoria) => v.orcado === 0n && v.realizado === 0n && v.comprometido === 0n;
  return [...porCategoria.values()]
    .filter((c) => !vazio(c))
    .map(({ porCc, ...c }) => ({ ...c, centros: [...porCc.values()].filter((x) => !vazio(x)).sort(porMaiorRealizado) }))
    // Sem código (o lançamento sem categoria) no fim: um "~" de sentinela não
    // serve, porque a comparação por idioma põe pontuação antes dos números.
    .sort((a, b) =>
      Number(a.codigo === null) - Number(b.codigo === null)
      || (a.codigo ?? "").localeCompare(b.codigo ?? "", undefined, { numeric: true })
      || a.nome.localeCompare(b.nome));
}

/**
 * A faixa de consumo, a mesma régua do Orçado vs Realizado: o usado (realizado
 * + comprometido) sobre o orçado, atenção a partir de 80% e acima de 100%.
 * Gasto sem orçado nenhum é "Sem orçamento", não "acima": não havia teto.
 */
export function situacaoCategoria(v: ValoresCategoria): { tom: Tom; rotulo: string } | null {
  const usado = v.realizado + v.comprometido;
  if (v.orcado === 0n) return usado > 0n ? { tom: "neutro", rotulo: "Sem orçamento" } : null;
  const pct = Number((usado * 10000n) / v.orcado) / 100;
  return pct > 100
    ? { tom: "risco", rotulo: "Acima" }
    : pct >= 80
      ? { tom: "atencao", rotulo: "Atenção" }
      : { tom: "ok", rotulo: "Dentro" };
}
